import { useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Reply } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Panel, StatusBadge } from "@/components/app/common";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

function Composer({ orderId, parentId, onPosted, testid }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const post = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { await api.post(`/orders/${orderId}/remarks`, { message: msg, parent_id: parentId || null }); setMsg(""); onPosted(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col sm:flex-row gap-2" data-testid={testid}>
      <Textarea rows={2} className="flex-1" placeholder={parentId ? "Write a reply…" : "Add an internal remark for this LM…"} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid={`${testid}-input`} />
      <Button size="sm" disabled={busy || !msg.trim()} onClick={post} className="self-end" data-testid={`${testid}-post`}>{parentId ? "Reply" : "Add remark"}</Button>
    </div>
  );
}

function Entry({ r, replies, orderId, reload }) {
  const [reply, setReply] = useState(false);
  const qc = r.kind === "qc";
  return (
    <div className={`rounded border px-3 py-2 ${qc ? "border-sky-200 bg-sky-50/60" : "border-slate-200 bg-white"}`} data-testid={`lm-remark-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
        <b className="text-slate-800">{r.by?.name}</b><span>{fmtDateTime(r.at)}</span>{qc && <StatusBadge status={r.qc_status} />}
        {!qc && <button className="ml-auto inline-flex items-center gap-1 text-sky-700 hover:underline" onClick={() => setReply(!reply)} data-testid={`lm-remark-reply-${r.id}`}><Reply className="h-3 w-3" />Reply</button>}
      </div>
      <p className="mt-1 text-sm whitespace-pre-wrap text-slate-800">{r.message}</p>
      {replies.length > 0 && <div className="mt-2 ml-3 space-y-2 border-l-2 border-slate-200 pl-3">{replies.map((x) => (
        <div key={x.id} className="text-sm" data-testid={`lm-remark-${x.id}`}><div className="text-[11px] text-slate-500"><b className="text-slate-800">{x.by?.name}</b> · {fmtDateTime(x.at)}</div><p className="whitespace-pre-wrap">{x.message}</p></div>
      ))}</div>}
      {reply && <div className="mt-2"><Composer orderId={orderId} parentId={r.id} onPosted={() => { setReply(false); reload(); }} testid={`lm-reply-${r.id}`} /></div>}
    </div>
  );
}

export function LmRemarks({ orderId, refreshKey }) {
  const { data, reload } = useFetch(`/orders/${orderId}/remarks?k=${refreshKey || 0}`);
  const rows = data || [];
  const top = rows.filter((r) => !r.parent_id).reverse();
  return (
    <Panel title={<span className="inline-flex items-center gap-2"><MessageSquare className="h-4 w-4" />Internal remarks & discussion ({rows.length})</span>} className="mt-4" testid="lm-remarks-panel">
      <div className="space-y-3">
        <Composer orderId={orderId} onPosted={reload} testid="lm-remark-new" />
        <p className="text-[11px] text-slate-500">Running record for this LM. Ready for QC submissions and QC decisions (with remarks) appear here automatically.</p>
        <div className="space-y-2 max-h-[480px] overflow-y-auto" data-testid="lm-remarks-list">
          {top.map((r) => <Entry key={r.id} r={r} replies={rows.filter((x) => x.parent_id === r.id)} orderId={orderId} reload={reload} />)}
          {!top.length && <p className="text-xs text-slate-400">No remarks yet.</p>}
        </div>
      </div>
    </Panel>
  );
}
